/**
 * @file GenderIcon.tsx
 * @brief Gender avatar for picker rows — mirrors mobile `VZGenderPicker`.
 *
 * @author DES
 * @date 19-06-2026
 */

import { Circle, EyeOff, UserRound } from "lucide-react";
import { cn } from "@/shared/theme/cn";

interface GenderIconProps {
  code?: string | null;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const WRAP: Record<NonNullable<GenderIconProps["size"]>, string> = {
  sm: "h-5 w-5",
  md: "h-10 w-10",
  lg: "h-14 w-14",
};

const SYMBOL: Record<NonNullable<GenderIconProps["size"]>, string> = {
  sm: "text-sm",
  md: "text-xl",
  lg: "text-3xl",
};

export function GenderIcon({ code, size = "md", className }: GenderIconProps) {
  const key = code?.toLowerCase();

  let glyph = <UserRound className="h-4 w-4 text-brand" aria-hidden />;

  if (key === "male") {
    glyph = (
      <span className={cn("font-semibold leading-none text-brand", SYMBOL[size])}>
        ♂
      </span>
    );
  } else if (key === "female") {
    glyph = (
      <span className={cn("font-semibold leading-none text-brand", SYMBOL[size])}>
        ♀
      </span>
    );
  } else if (key === "other") {
    glyph = (
      <Circle
        className={cn(
          "text-brand",
          size === "sm" ? "h-3 w-3" : size === "lg" ? "h-7 w-7" : "h-5 w-5",
        )}
        aria-hidden
      />
    );
  } else if (key === "prefer_not_to_say") {
    glyph = (
      <EyeOff
        className={cn(
          "text-brand",
          size === "sm" ? "h-3 w-3" : size === "lg" ? "h-7 w-7" : "h-5 w-5",
        )}
        aria-hidden
      />
    );
  } else if (size === "sm") {
    glyph = <UserRound className="h-3 w-3 text-muted-foreground" aria-hidden />;
  }

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40",
        WRAP[size],
        className,
      )}
    >
      {glyph}
    </span>
  );
}
